/**
 * Interaction types - using nexus-public-api library types directly
 * Following nexus-shell pattern
 */

import { NexusCrm } from '@open/nexus-public-api'

// ============================================
// Extend library types with UI-specific fields
// ============================================

import type {
  Sentiment,
  CategorySuggestion,
  PrioritySuggestion,
  CustomerIntent,
} from '@repo/ai-analytics'

export interface InteractionAIAnalysis {
  sentiment: Sentiment
  sentimentScore: number
  suggestedCategory?: CategorySuggestion
  suggestedPriority?: PrioritySuggestion
  customerIntent?: CustomerIntent
  keyTopics?: string[]
  analyzedAt: string
}

// Omit fields the UI overrides as optional (`customerId`) — Swift's
// inferred type makes `customerId` required, but the web treats it
// as optional during enrichment passes. Wire-side Zod still validates
// strictly; this just relaxes the local UI type for that one field.
export interface Interaction
  extends Omit<NexusCrm.CrmInteractionSchemeType, 'customerId'> {
  // AI Analysis (populated on-demand)
  aiAnalysis?: InteractionAIAnalysis

  // Map library fields to UI aliases for backwards compatibility
  customerId?: string // alias for customer
  customerName?: string // UI-enriched field
  customerEmail?: string
  customerPhone?: string
  customerAvatar?: string
  customerCompany?: string

  assignedTo?: string // alias for assignee
  assignedToName?: string // UI-enriched field
  assignedToAvatar?: string
  assignedAt?: Date | string | number
  agentId?: string // alias for assignee
  agentName?: string // alias for assignedToName

  // UI type mapping (library uses NexusCrm.CrmInteractionStatusNative)
  type?: InteractionType
  direction?: InteractionDirection
  outcome?: string

  // Queue info (library has queue: string)
  queueId?: string // alias for queue
  queueName?: string // UI-enriched field
  queuePosition?: number

  // Content fields
  description?: string
  lastMessage?: string
  lastMessageAt?: Date | string | number
  unreadCount?: number

  // Category/tags
  category?: string
  tagIds?: string[] // Hierarchical tag IDs (from tag store)

  // SLA fields
  slaDeadline?: Date | string | number
  slaWarning?: boolean
  slaExpired?: boolean
  slaRemainingMinutes?: number
  slaStatus?: 'ok' | 'warning' | 'expired'

  // Server-authoritative SLA timestamps (Swift Interaction.firstResponseAt /
  // .resolvedAt, ms — `z.number().optional()` in the lib, so typed as
  // `number` to stay assignable to the extended lib interface). Gap #9:
  // SLA monitor must read these directly — NOT re-derive first-response
  // from `lastMessageAt` (moves on every message incl. inbound) or
  // resolution from `closedAt`.
  firstResponseAt?: number
  resolvedAt?: number

  // Timestamps (library has createdAt: number, updatedAt?: number)
  closedAt?: Date | string | number
  scheduledAt?: Date | string | number
  startedAt?: Date | string | number
  completedAt?: Date | string | number

  // Channel info
  channelId?: string
  activeChannelId?: string
  customerChannels?: CustomerChannel[]

  // Duration
  duration?: number

  // Attachments (UI-specific)
  attachments?: InteractionAttachment[]

  // Metadata
  metadata?: Record<string, unknown>
}

export interface InteractionMessage extends NexusCrm.CrmMessageSchemeType {
  // UI-only enrichment fields layered on the canonical Swift message
  // (`mid`/`interactionId`/`type`/`status`/… come from the base type).
  senderId?: string // resolved sender (memberId/customerId)
  senderName?: string // UI-enriched
  senderRole?: 'customer' | 'agent' | 'system'
  senderType?: 'customer' | 'agent' | 'system' // alias for senderRole
  senderAvatar?: string
  content?: string // alias for body
  contentType?: 'text' | 'image' | 'file' | 'system'
  attachments?: InteractionAttachment[]
  timestamp?: Date | string | number // alias for createdAt
  isRead?: boolean
  isInternal?: boolean
  channel?: string // library already has this
  customerChannelId?: string // alias for customerChannel
  metadata?: Record<string, unknown>
}


// ============================================
// UI-specific types not in library
// ============================================

export type InteractionStatus =
  | 'active'
  | 'waiting'
  | 'closed'
  | 'transferred'
  | 'in_progress'
  | 'scheduled'
  | 'completed'
  | 'cancelled'
  | 'missed'
  // Library native statuses (Swift-aligned set, v1.0.326+)
  | 'open'
  | 'pending'
  | 'resolved'
  | 'snoozed'
  // Legacy library status — kept for back-compat with stored data;
  // map to `resolved` for new code paths.
  | 'solved'

export type InteractionType =
  | 'chat'
  | 'call'
  | 'email'
  | 'meeting'
  | 'sms'
  | 'note'
  | 'task'
  | 'social'

export type InteractionPriority = 'low' | 'normal' | 'high' | 'urgent'
export type InteractionDirection = 'inbound' | 'outbound'

export interface InteractionAttachment {
  id: string
  name: string
  type: string
  size: number
  url: string
  thumbnailUrl?: string
}

export interface InteractionNote {
  id: string
  interactionId: string
  authorId: string
  authorName: string
  authorAvatar?: string
  content: string
  isPrivate: boolean
  createdAt: Date
  updatedAt: Date
}

export type InteractionActivityType =
  | 'created'
  | 'assigned'
  | 'transferred'
  | 'status_changed'
  | 'priority_changed'
  | 'tag_added'
  | 'tag_removed'
  | 'note_added'
  | 'message_sent'
  | 'closed'
  | 'reopened'
  // CSAT/Reminder/Snooze/Time tracking
  | 'csat_requested'
  | 'csat_submitted'
  | 'reminder_set'
  | 'reminder_cleared'
  | 'snoozed'
  | 'unsnoozed'
  | 'time_added'
  | 'time_removed'
  | 'field_updated'

export interface InteractionActivity {
  id: string
  interactionId: string
  type: InteractionActivityType
  userId: string
  userName: string
  userAvatar?: string
  description: string
  timestamp: Date
  metadata?: {
    oldValue?: unknown
    newValue?: unknown
    [key: string]: unknown
  }
}

export interface InteractionTransfer {
  interactionId: string
  fromUserId: string
  fromUserName: string
  toUserId: string
  toUserName: string
  reason?: string
  notes?: string
  timestamp: Date
}

export interface InteractionMerge {
  primaryInteractionId: string
  secondaryInteractionIds: string[]
  reason?: string
  notes?: string
  mergedBy: string
  mergedAt: Date
}

export interface SLAPolicy {
  id: string
  name: string
  description?: string
  responseTime: {
    urgent: number
    high: number
    normal: number
    low: number
  }
  resolutionTime: {
    urgent: number
    high: number
    normal: number
    low: number
  }
  warningThreshold: number
  businessHoursOnly: boolean
  businessHours?: {
    start: string
    end: string
    timezone: string
    workingDays: number[]
  }
  isActive: boolean
  createdAt: Date
  updatedAt: Date
}

export interface InteractionQueue {
  id: string
  name: string
  description?: string
  icon?: string
  color?: string
  assignmentStrategy: 'round_robin' | 'least_active' | 'manual' | 'skill_based'
  maxConcurrentInteractions?: number
  memberIds: string[]
  slaPolicy?: SLAPolicy
  slaPolicyId?: string
  defaultPriority: InteractionPriority
  isActive: boolean
  isPaused: boolean
  activeCount: number
  waitingCount: number
  averageWaitTime: number
  averageHandleTime: number
  createdAt: Date
  updatedAt: Date
}

export interface InteractionFilters {
  status?: InteractionStatus[]
  type?: InteractionType[]
  priority?: InteractionPriority[]
  assignedTo?: string[]
  queueId?: string[]
  tags?: string[]
  searchQuery?: string
  dateFrom?: Date
  dateTo?: Date
  hasSLAWarning?: boolean
  hasSLAExpired?: boolean
  unassignedOnly?: boolean
}

export interface InteractionStatistics {
  total: number
  active: number
  waiting: number
  closed: number
  transferred: number
  byType: {
    chat: number
    call: number
    email: number
  }
  byPriority: {
    urgent: number
    high: number
    normal: number
    low: number
  }
  sla: {
    withinSLA: number
    atRisk: number
    breached: number
  }
  averageResponseTime: number
  averageHandleTime: number
  averageWaitTime: number
}

export type AgentStatus = 'available' | 'busy' | 'away' | 'offline'

// ============================================
// AI Types
// ============================================

export type AIProviderType = 'openai' | 'anthropic' | 'local' | 'azure' | 'custom'
export type ChatbotTone = 'professional' | 'friendly' | 'formal' | 'casual' | 'empathetic'
export type KnowledgeType = 'faq' | 'article' | 'document' | 'url' | 'custom'

export interface Bot {
  id: string
  name: string
  description?: string
  provider: AIProviderType
  tone: ChatbotTone
  enabled: boolean
  model?: string
  temperature?: number
  maxTokens?: number
  systemPrompt?: string
  knowledgeBaseIds?: string[]
  fallbackMessage?: string
  triggerKeywords?: string[]
  maxConversationLength?: number
  isDefault?: boolean
  createdAt: string | number
  updatedAt?: string | number
}

export interface BotConfig {
  botId: string
  queueIds?: string[]
  enableAutoRespond: boolean
  respondToOffHours: boolean
  handoffThreshold?: number
  handoffKeywords?: string[]
  greeting?: string
  farewell?: string
}

export interface Module {
  id: string
  name: string
  type: 'routing' | 'classification' | 'sentiment' | 'summary' | 'translation' | 'custom'
  description?: string
  config: Record<string, unknown>
  enabled: boolean
  priority?: number
  createdAt: string | number
  updatedAt?: string | number
}

export interface KnowledgeBase {
  id: string
  name: string
  description?: string
  type: KnowledgeType
  sources: KnowledgeSource[]
  vectorStoreId?: string
  embeddingModel?: string
  chunkSize?: number
  chunkOverlap?: number
  isIndexed: boolean
  lastIndexedAt?: string | number
  documentCount: number
  createdAt: string | number
  updatedAt?: string | number
}

export interface KnowledgeSource {
  id: string
  knowledgeBaseId: string
  type: 'file' | 'url' | 'text'
  name: string
  content?: string
  url?: string
  fileId?: string
  metadata?: Record<string, unknown>
  status: 'pending' | 'processing' | 'indexed' | 'failed'
  errorMessage?: string
  createdAt: string | number
  updatedAt?: string | number
}

export interface AIResponse {
  id: string
  interactionId: string
  botId?: string
  prompt: string
  response: string
  confidence: number
  sources?: Array<{
    sourceId: string
    sourceName: string
    relevance: number
    excerpt?: string
  }>
  tokensUsed?: number
  latencyMs?: number
  wasUsed: boolean
  feedback?: 'positive' | 'negative' | null
  createdAt: string | number
}

export interface SentimentAnalysis {
  interactionId: string
  messageId?: string
  sentiment: 'positive' | 'negative' | 'neutral' | 'mixed'
  score: number // -1 to 1
  confidence: number // 0 to 1
  emotions?: Array<{
    emotion: 'joy' | 'anger' | 'sadness' | 'fear' | 'surprise' | 'disgust'
    score: number
  }>
  topics?: string[]
  urgency?: 'low' | 'medium' | 'high' | 'critical'
  createdAt: string | number
}

export interface AgentInfo {
  id: string
  name: string
  email: string
  avatar?: string
  status: AgentStatus
  currentInteractions: number
  maxConcurrentInteractions: number
  queueIds: string[]
  skills: string[]
  isOnline: boolean
  lastSeenAt?: Date
}

export type ChannelType =
  | 'telegram'
  | 'whatsapp'
  | 'facebook'
  | 'instagram'
  | 'email'
  | 'phone'
  | 'sms'
  | 'chat'
  | 'web'
  | 'internal'

export interface CustomerChannel {
  id: string
  type: ChannelType
  identifier: string
  displayName: string
  isActive: boolean
}

/**
 * A workspace (org-side) channel the agent can send FROM — mirrors
 * nexus-shell's "FROM" selector. Sourced from `crm.channel.list`
 * (mapped in CrmService.loadChannels): id + type + label.
 */
export interface WorkspaceChannel {
  id: string
  type: ChannelType
  name: string
  /** Owning member/agent label if the backend exposes it. */
  memberName?: string
}

/**
 * Utility functions for working with interactions (nexus-shell pattern)
 */

export function getInteractionId(interaction: NexusCrm.CrmInteractionSchemeType): string {
  return interaction.id
}

export function getInteractionStatus(
  interaction: NexusCrm.CrmInteractionSchemeType
): NexusCrm.CrmInteractionStatusNative {
  return interaction.status
}

export function getInteractionPriority(
  interaction: NexusCrm.CrmInteractionSchemeType
): NexusCrm.CrmInteractionPriorityNative | undefined {
  return interaction.priority
}

export function getInteractionAssignee(
  interaction: NexusCrm.CrmInteractionSchemeType
): string | undefined {
  return interaction.assignee
}

export function getInteractionCustomer(
  interaction: NexusCrm.CrmInteractionSchemeType
): string | undefined {
  return interaction.customer
}

export function getInteractionTitle(
  interaction: NexusCrm.CrmInteractionSchemeType
): string | undefined {
  return interaction.title
}

export function getInteractionTags(
  interaction: NexusCrm.CrmInteractionSchemeType
): string[] {
  return interaction.tags || []
}

export function getInteractionMessages(
  interaction: NexusCrm.CrmInteractionSchemeType
): NexusCrm.CrmMessageSchemeType[] {
  return interaction.messages || []
}

export function getInteractionCreatedAt(
  interaction: NexusCrm.CrmInteractionSchemeType
): number {
  return interaction.createdAt
}

export function getInteractionUpdatedAt(
  interaction: NexusCrm.CrmInteractionSchemeType
): number | undefined {
  return interaction.updatedAt
}

export function getInteractionQueue(
  interaction: NexusCrm.CrmInteractionSchemeType
): string | undefined {
  return interaction.queue
}

export function getInteractionBusiness(
  interaction: NexusCrm.CrmInteractionSchemeType
): string | undefined {
  return interaction.business
}

export function getInteractionCallId(
  interaction: NexusCrm.CrmInteractionSchemeType
): string | undefined {
  return interaction.callID
}

export function getInteractionCall(interaction: NexusCrm.CrmInteractionSchemeType) {
  return interaction.call
}

// Message utility functions

export function getMessageId(message: NexusCrm.CrmMessageSchemeType): string {
  return message.mid ?? message.id ?? ''
}

export function getMessageInteractionId(message: NexusCrm.CrmMessageSchemeType): string {
  return message.interactionId ?? message.interactionID ?? ''
}

export function getMessageBody(message: NexusCrm.CrmMessageSchemeType): string | undefined {
  return message.body
}

export function getMessageType(message: NexusCrm.CrmMessageSchemeType): NexusCrm.CrmMessageTypeNative {
  return message.type
}

export function getMessageStatus(message: NexusCrm.CrmMessageSchemeType): NexusCrm.CrmMessageStatusNative {
  return message.status
}

// Swift `InteractionMessage`: outbound carries `memberId` (the agent),
// inbound has neither (customer-originated) but may carry `customerId`.
export function getMessageSenderId(message: NexusCrm.CrmMessageSchemeType): string | undefined {
  return message.memberId ?? message.customerId ?? message.memberID ?? message.customerID
}

export function getMessageChannel(message: NexusCrm.CrmMessageSchemeType): string | undefined {
  return message.channelId ?? message.channel
}

export function getMessageCustomerChannel(message: NexusCrm.CrmMessageSchemeType): string | undefined {
  return message.customerChannelId ?? message.customerChannel
}

export function getMessageCreatedAt(message: NexusCrm.CrmMessageSchemeType): number | undefined {
  return message.createdAt
}

// Swift collapsed every automated event into `type:"system"` with a
// human-readable `body` (no structured payload, no numeric 1001-1008).
export function isSystemMessage(message: NexusCrm.CrmMessageSchemeType): boolean {
  return message.type === NexusCrm.CrmMessageTypeNative.system
}

// Sort messages by creation time (ascending - oldest first)
export function sortMessagesByTimeAsc(
  messages: NexusCrm.CrmMessageSchemeType[]
): NexusCrm.CrmMessageSchemeType[] {
  return [...messages].sort((a, b) => {
    const timeA = a.createdAt || 0
    const timeB = b.createdAt || 0
    return timeA - timeB
  })
}

// Sort messages by creation time (descending - newest first)
export function sortMessagesByTimeDesc(
  messages: NexusCrm.CrmMessageSchemeType[]
): NexusCrm.CrmMessageSchemeType[] {
  return [...messages].sort((a, b) => {
    const timeA = a.createdAt || 0
    const timeB = b.createdAt || 0
    return timeB - timeA
  })
}

/**
 * Map library status to UI status
 * Library: open | pending | solved
 * UI: active | waiting | closed | etc.
 */
export function mapLibraryStatusToUi(
  status: NexusCrm.CrmInteractionStatusNative
): InteractionStatus {
  switch (status) {
    case NexusCrm.CrmInteractionStatusNative.open:
      return 'active'
    case NexusCrm.CrmInteractionStatusNative.pending:
      return 'waiting'
    case NexusCrm.CrmInteractionStatusNative.resolved:
      // Gap #12 FIXED: surface `resolved` distinctly (post-resolution /
      // awaiting-CSAT window) rather than collapsing into final `closed`.
      // `'resolved'` is a valid InteractionStatus member and consumers
      // (interaction-card colors/known-keys) already handle it.
      return 'resolved'
    case NexusCrm.CrmInteractionStatusNative.closed:
      return 'closed'
    case NexusCrm.CrmInteractionStatusNative.snoozed:
      // Must NOT collapse to `waiting` — that erased snooze state and
      // made the snooze feature structurally impossible (badge logic +
      // un-snooze keyed off a status that never surfaced). `'snoozed'`
      // is a valid InteractionStatus member; surface it as-is.
      return 'snoozed'
    case NexusCrm.CrmInteractionStatusNative.transferred:
      return 'transferred'
    default:
      return status as InteractionStatus
  }
}

/**
 * Map UI status to library status (Swift-aligned, v1.0.326+).
 */
export function mapUiStatusToLibrary(
  status: InteractionStatus
): NexusCrm.CrmInteractionStatusNative {
  switch (status) {
    case 'active':
    case 'in_progress':
      return NexusCrm.CrmInteractionStatusNative.open
    case 'snoozed':
      // Critical: must round-trip back to `snoozed`, not `pending` —
      // mapping it to pending silently un-snoozed any interaction whose
      // status was written back through this path.
      return NexusCrm.CrmInteractionStatusNative.snoozed
    case 'waiting':
    case 'scheduled':
      return NexusCrm.CrmInteractionStatusNative.pending
    case 'closed':
    case 'completed':
    case 'cancelled':
    case 'missed':
      return NexusCrm.CrmInteractionStatusNative.resolved
    case 'transferred':
      return NexusCrm.CrmInteractionStatusNative.transferred
    // Already library status
    case 'open':
      return NexusCrm.CrmInteractionStatusNative.open
    case 'pending':
      return NexusCrm.CrmInteractionStatusNative.pending
    case 'resolved':
    case 'solved': // legacy alias
      return NexusCrm.CrmInteractionStatusNative.resolved
    default:
      return NexusCrm.CrmInteractionStatusNative.open
  }
}

/**
 * Map library priority to UI priority.
 * Library (v1.0.326+, Swift-aligned): low / normal / high / urgent (string).
 * Was previously a numeric enum (`low=0/mid=1/unknown=2/high=3`).
 * UI: low | normal | high | urgent
 */
export function mapLibraryPriorityToUi(
  priority: NexusCrm.CrmInteractionPriorityNative
): InteractionPriority {
  switch (priority) {
    case NexusCrm.CrmInteractionPriorityNative.low:
      return 'low'
    case NexusCrm.CrmInteractionPriorityNative.normal:
      return 'normal'
    case NexusCrm.CrmInteractionPriorityNative.high:
      return 'high'
    case NexusCrm.CrmInteractionPriorityNative.urgent:
      return 'urgent'
    default:
      return 'normal'
  }
}

/**
 * Map UI priority to library priority.
 */
export function mapUiPriorityToLibrary(
  priority: InteractionPriority
): NexusCrm.CrmInteractionPriorityNative {
  switch (priority) {
    case 'low':
      return NexusCrm.CrmInteractionPriorityNative.low
    case 'normal':
      return NexusCrm.CrmInteractionPriorityNative.normal
    case 'high':
      return NexusCrm.CrmInteractionPriorityNative.high
    case 'urgent':
      return NexusCrm.CrmInteractionPriorityNative.urgent
    default:
      return NexusCrm.CrmInteractionPriorityNative.normal
  }
}

// ============================================
// CSAT, Reminder, Snooze, Time Tracking Types
// ============================================

/**
 * Customer Satisfaction (CSAT) rating
 */
export interface CsatRating {
  interactionId: string
  score: number // 1-5 or 1-10 scale
  comment?: string
  submittedAt?: string | number
  requestedAt?: string | number
}

/**
 * Interaction reminder
 */
export interface InteractionReminder {
  interactionId: string
  reminderAt: string // ISO datetime
  note?: string
  createdBy?: string
  createdAt?: string | number
}

/**
 * Interaction snooze info
 */
export interface InteractionSnooze {
  interactionId: string
  snoozedUntil: string // ISO datetime
  reason?: string
  snoozedBy?: string
  snoozedAt?: string | number
}

/**
 * Time tracking entry for interaction
 */
export interface InteractionTimeEntry {
  id: string
  interactionID: string
  memberID: string
  memberName?: string
  duration: number // seconds
  note?: string
  createdAt: string
}

/**
 * Custom field definition for interactions
 */
export interface InteractionFieldDefinition {
  id: string
  name: string
  type: 'text' | 'number' | 'date' | 'select' | 'multiselect' | 'boolean'
  required?: boolean
  options?: string[] // for select/multiselect types
  defaultValue?: unknown
  description?: string
}

/**
 * Custom field value on an interaction
 */
export interface InteractionFieldValue {
  fieldId: string
  fieldName?: string
  value: unknown
}

